import { PayrollPeriod, timeZonePreference } from '@numeo/types-core'
import { DateTime, Zone } from 'luxon'
import { getApplicationSegmentBounds, getBoardWeekAnchor, isGrossBoardOnAllWeeks } from 'utils/payrollSchedule'
import { anchorWeekday, schedulerTimeZones } from './common'

/*
 * The board's week starts on the company's payroll period start day, so the gross a dispatcher reads off
 * the board covers the same days as the payroll statement it is checked against. The default is the
 * historical Monday-Sunday grid, which is also what a company that has never configured payroll gets —
 * for them nothing about the board changes.
 *
 * A company can switch that coupling's TIMING with `grossBoardWeekMode`: the same period start day, but
 * applied to every week at once instead of from the next period. See `getBoardWeekRange`.
 */

/**
 * Converts a UTC date string to a DateTime object in the user's timezone
 * @param utcDateString - ISO string in UTC format
 * @param userTimeZone - User's timezone key (ET, CT, MT, PT)
 * @returns DateTime object in user's timezone
 */
export const convertUTCToUserTimezone = (utcDateString: string, userTimeZone: timeZonePreference): DateTime => {
    const timezone = schedulerTimeZones[userTimeZone]
    return DateTime.fromISO(utcDateString).setZone(timezone)
}

/**
 * Converts a DateTime object in user's timezone to UTC
 * @param dateTime - DateTime object in user's timezone
 * @returns DateTime object in UTC
 */
export const convertUserTimezoneToUTC = (dateTime: DateTime): DateTime => {
    return dateTime.toUTC()
}
/**
 * Gets the start and end of the week in user's timezone
 * @param selectedDateIso - UTC date string
 * @param userTimeZone - User's timezone key
 * @param anchor - Company period start day; defaults to Monday-Sunday
 * @returns Object with start and end DateTime objects in user's timezone
 */
export const getWeekRangeInUserTimezone = (selectedDateIso: string, userTimeZone: timeZonePreference, anchor?: PayrollPeriod) => {
    // Convert UTC selectedDate to user's timezone for calculations
    const dateInUserTZ = convertUTCToUserTimezone(selectedDateIso, userTimeZone)

    // Days back to the anchor weekday: 0 when the selected day IS the anchor, 6 the day before it.
    const daysToSubtract = (dateInUserTZ.weekday - anchorWeekday(anchor) + 7) % 7

    // Calculate week start (anchor day) and end (6 days later) in user's timezone
    const weekStartInUserTZ = dateInUserTZ.minus({ days: daysToSubtract }).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    const weekEndInUserTZ = weekStartInUserTZ.plus({ days: 6 }).set({ hour: 23, minute: 59, second: 59, millisecond: 999 })

    return {
        start: weekStartInUserTZ,
        end: weekEndInUserTZ,
    }
}

/**
 * Gets an array of 7 DateTime objects representing the days of the week
 * @param selectedDateIso - UTC date string
 * @param userTimeZone - User's timezone key
 * @returns Array of 7 DateTime objects in user's timezone
 */
export const getWeekDaysInUserTimezone = (selectedDateIso: string, userTimeZone: timeZonePreference, anchor?: PayrollPeriod): DateTime[] => {
    const { start } = getWeekRangeInUserTimezone(selectedDateIso, userTimeZone, anchor)
    return [...Array(7)].map((_, i) => start.plus({ days: i }))
}

/**
 * Converts a Date object to a DateTime object in user's timezone
 * @param date - Date object
 * @param userTimeZone - User's timezone key
 * @returns DateTime object in user's timezone
 */
export const convertDateToUserTimezone = (date: Date, userTimeZone: timeZonePreference): DateTime => {
    const timezone = schedulerTimeZones[userTimeZone]
    return DateTime.fromJSDate(date).setZone(timezone)
}

/**
 * Converts a DateTime object in user's timezone to a Date object
 * @param dateTime - DateTime object in user's timezone
 * @returns Date object
 */
export const convertUserTimezoneToDate = (dateTime: DateTime): Date => {
    return dateTime.toJSDate()
}

/**
 * Formats a DateTime object for display in the user's timezone
 * @param dateTime - DateTime object
 * @param format - Luxon format string
 * @returns Formatted string
 */
export const formatInUserTimezone = (dateTime: DateTime, format: string = 'yyyy-MM-dd'): string => {
    return dateTime.toFormat(format)
}

/**
 * Gets the current time in user's timezone
 * @param userTimeZone - User's timezone key
 * @returns DateTime object representing current time in user's timezone
 */
export const getCurrentTimeInUserTimezone = (userTimeZone: timeZonePreference): DateTime => {
    const timezone = schedulerTimeZones[userTimeZone]
    return DateTime.now().setZone(timezone)
}

export function getDateOnlyInUserTimezone(datetimeObj: DateTime, userTimeZone: timeZonePreference): Date {
    const timezone = schedulerTimeZones[userTimeZone]
    return datetimeObj.setZone(timezone).startOf('day').toJSDate()
}

/*
 * `convertDateToUserTimezoneDate` used to live here and was deleted: it was `DateTime.fromJSDate(d)
 * .setZone(tz).toJSDate()`, which returns the SAME instant — `setZone` only changes how a DateTime is
 * presented, and `toJSDate` throws that presentation away. It was identity in production and mocked to
 * identity in its own tests, so it never converted anything; it only made `isLoadOverlappingRange` look
 * timezone-aware. Week bounds already carry the user's zone (they come from `weekDays[0].startOf('day')`
 * in `useWeekRange`), so comparing them to a load's raw instant is correct without it.
 */

/**
 * A payroll cadence seam — the day a schedule segment takes effect — as a day on the board.
 *
 * The boundary is stored as a UTC start-of-day. Read it as that same CALENDAR DAY in the board's zone
 * rather than as an instant: the board's columns are whole days in that zone, and converting the instant
 * would put the seam mid-day for anyone west of UTC, splitting a column in half.
 *
 * Shared rather than inlined at each use. Both the week's own clipping and the "new week starts …" notice
 * have to place the seam on the same day — two copies of this conversion could drift apart and put the
 * notice on a different day than the board actually changes on.
 */
export const segmentSeamDay = (boundary: Date, zone: Zone | string): DateTime =>
    DateTime.fromObject({ year: boundary.getUTCFullYear(), month: boundary.getUTCMonth() + 1, day: boundary.getUTCDate() }, { zone }).startOf('day')

/**
 * The board's week for `selectedDateIso`: anchored on the company's payroll period start day and clipped
 * at a cadence seam, exactly the way payroll's `resolvePeriod` builds a period.
 *
 * Unless the company put the board on ALL_WEEKS, in which case neither of those applies. That mode
 * exists to answer "show my gross on THIS week, all of it" — so the anchor is resolved once for every
 * week rather than at the viewed date, and the seam clipping below is skipped outright. Clipping is what
 * keeps the board aligned to a payroll period; a board that no longer tracks payroll periods has no seam
 * to clip at, and clipping anyway would hand back a short week (and, on the week the payroll cadence
 * changed, a week the user cannot explain from a setting they did not touch).
 *
 * Not exported: callers want {@link getBoardRange}, which also answers for a custom gross range. A caller
 * that reached past it would be asking "which week would the board be on" rather than "which days is the
 * board showing" — and those two differ precisely when a custom range is open.
 */
const getBoardWeekRange = (
    selectedDateIso: string,
    userTimeZone: timeZonePreference,
    application: Parameters<typeof getBoardWeekAnchor>[0]
): { start: DateTime; end: DateTime } => {
    const parsed = new Date(selectedDateIso)
    const at = Number.isNaN(parsed.getTime()) ? new Date() : parsed
    const { start, end } = getWeekRangeInUserTimezone(selectedDateIso, userTimeZone, getBoardWeekAnchor(application, at))

    // ALL_WEEKS: one anchor, every week, always a full 7 days. Returning before the seam logic is the
    // whole difference between this mode and the payroll-following one — clipping exists to keep a week
    // inside one payroll period, and this mode has deliberately stopped drawing periods.
    if (isGrossBoardOnAllWeeks(application)) return { start, end }

    const bounds = getApplicationSegmentBounds(application, at)
    const seam = (boundary: Date | null) => (boundary ? segmentSeamDay(boundary, schedulerTimeZones[userTimeZone]) : null)

    // Both clips are no-ops on every week that does not touch a seam — and on every company that has never
    // changed its cadence, which has a single segment and so no `until` at all.
    const segmentStart = seam(bounds.from)
    const lastInstantBeforeNext = seam(bounds.until)?.minus({ milliseconds: 1 })
    return {
        start: segmentStart && segmentStart > start ? segmentStart : start,
        end: lastInstantBeforeNext && lastInstantBeforeNext < end ? lastInstantBeforeNext : end,
    }
}

/**
 * The days the board is actually showing — a user-picked gross date range when one is open, otherwise the
 * anchored week.
 *
 * The single definition of that. The grid, the window sent to the server and the live-update middleware
 * all read it from here: three copies of this decision would drift, and a live update that answered on the
 * anchored week while the grid drew a custom range would add or drop a load the user can plainly see is on
 * the other side of the boundary.
 */
export const getBoardRange = (
    selectedDateIso: string,
    userTimeZone: timeZonePreference,
    application: Parameters<typeof getBoardWeekAnchor>[0],
    grossDateRange?: { startDate: string; endDate: string } | null
): { start: DateTime; end: DateTime; isCustomRange: boolean } => {
    if (grossDateRange) {
        const zone = schedulerTimeZones[userTimeZone]
        return {
            start: DateTime.fromISO(grossDateRange.startDate, { zone }).startOf('day'),
            end: DateTime.fromISO(grossDateRange.endDate, { zone }).endOf('day'),
            isCustomRange: true,
        }
    }
    return { ...getBoardWeekRange(selectedDateIso, userTimeZone, application), isCustomRange: false }
}
